import React from "react";

import { Tooltip } from "@material-ui/core";
import UserNewIcon from "@material-ui/icons/RadioButtonUnchecked";
import UserPendingIcon from "@material-ui/icons/RemoveCircleOutline";
import UserApprovedIcon from "@material-ui/icons/CheckCircleOutline";
import UserSuspendedIcon from "@material-ui/icons/PauseCircleOutline";
import UserBlockedIcon from "@material-ui/icons/Block";
import NameMismatchIcon from "@material-ui/icons/ErrorOutline";
import { green, orange, red } from "@material-ui/core/colors";

import { UserStatus } from "../../../utils/constants";

/**
 * `blocked` is for records that keep the flag somewhere other than the record
 * itself — a carrier user's block lives on the junction row for one transload,
 * so the caller says which transload it means. Everything else reads
 * `record.blockAccess`, which is what the carrier user lists carry.
 */
export const UserStatusIcon = ({
  record,
  classes,
  blocked,
  nameMismatch,
  ...props
}) => {
  if (!record?.status) return null;

  const isBlocked = blocked ?? record.blockAccess;

  // The enrolled name disagrees with the OCR'd ID document — a possible
  // fake-name signal, so it outranks every status: whatever the account state,
  // the manager should look at this driver first.
  if (nameMismatch)
    return (
      <Tooltip title="Name mismatch" placement="left" arrow>
        <NameMismatchIcon className={classes} style={{ color: red[700] }} />
      </Tooltip>
    );

  // A blocked carrier user is registered and approved but still refused at sign
  // in, so the approved tick would be a lie. Only a suspended account outranks
  // it: that one holds in every transload, not just this one.
  if (isBlocked && record.status !== UserStatus.SUSPENDED)
    return (
      <Tooltip title="Blocked" placement="left" arrow>
        <UserBlockedIcon className={classes} style={{ color: red[700] }} />
      </Tooltip>
    );

  const icon = () => {
    switch (record.status) {
      case UserStatus.PENDING_REG:
        return (
          <Tooltip title="Pending Reg." placement="left" arrow>
            <UserPendingIcon
              className={classes}
              style={{ color: orange[700] }}
            />
          </Tooltip>
        );
      // ACTIVE is what an enrolled driver carries; APPROVED is what a user who
      // completed portal registration carries. Same meaning for this icon —
      // the account is good — and drivers have no registration to approve.
      case UserStatus.APPROVED:
      case UserStatus.ACTIVE:
        return (
          <Tooltip title="Active" placement="left" arrow>
            <UserApprovedIcon
              className={classes}
              style={{ color: green[700] }}
            />
          </Tooltip>
        );
      case UserStatus.SUSPENDED:
        return (
          <Tooltip title="Suspended" placement="left" arrow>
            <UserSuspendedIcon
              className={classes}
              style={{ color: red[700] }}
            />
          </Tooltip>
        );
      default:
        return (
          <Tooltip title="New" placement="left" arrow>
            <UserNewIcon className={classes} />
          </Tooltip>
        );
    }
  };

  return icon();
};
